import type { NotificationDetailAction } from "./notificationDetailActions"
import { resolveNotificationInternalPath } from "./notificationDetailNavigation"

export const PROFILE_BILLING_PATH = "/profile?tab=billing"

function pickStr(obj: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = obj[k]
    if (typeof v === "string" && v.trim()) return v.trim()
  }
  return undefined
}

function isAbsoluteHttpUrl(url: string): boolean {
  return /^https?:\/\//i.test(url.trim())
}

function resolveBillingPath(details: unknown): string {
  if (!details || typeof details !== "object" || Array.isArray(details)) {
    return PROFILE_BILLING_PATH
  }
  const obj = details as Record<string, unknown>
  const raw = pickStr(obj, ["action_url", "actionUrl", "billing_url", "billingUrl"])
  if (raw) {
    const internal = resolveNotificationInternalPath(raw)
    // Prefer in-app billing for dashboard CTAs; skip external Stripe hosted URLs.
    if (internal && (internal.startsWith("/profile") || internal.includes("tab=billing"))) {
      return internal.startsWith("/profile/billing")
        ? PROFILE_BILLING_PATH
        : internal
    }
  }
  return PROFILE_BILLING_PATH
}

/**
 * SCA / 3DS: open Stripe hosted invoice when present (same target as email).
 * Fall back to Profile → Billing if the link is missing.
 */
function resolveConfirmPaymentTarget(details: unknown): {
  path: string
  external?: boolean
} {
  if (!details || typeof details !== "object" || Array.isArray(details)) {
    return { path: PROFILE_BILLING_PATH }
  }
  const obj = details as Record<string, unknown>
  const hosted = pickStr(obj, [
    "hosted_invoice_url",
    "hostedInvoiceUrl",
    "action_url",
    "actionUrl",
  ])
  if (hosted && isAbsoluteHttpUrl(hosted)) {
    return { path: hosted.trim(), external: true }
  }
  return { path: resolveBillingPath(details) }
}

export type BillingDetailActionKind =
  | "payment_succeeded"
  | "payment_failed"
  | "payment_action_required"
  | "subscription_cancelled"
  | "subscription_updated"

const BILLING_ACTION_COPY: Record<
  BillingDetailActionKind,
  { id: string; buttonLabel: string }
> = {
  payment_succeeded: { id: "view-billing", buttonLabel: "View billing" },
  payment_failed: { id: "manage-payment", buttonLabel: "Manage payment" },
  payment_action_required: { id: "confirm-payment", buttonLabel: "Confirm payment" },
  subscription_cancelled: { id: "reactivate", buttonLabel: "Re-activate" },
  subscription_updated: { id: "view-billing", buttonLabel: "View billing" },
}

export function buildBillingDetailActions(
  details: unknown,
  kind: BillingDetailActionKind
): NotificationDetailAction[] {
  const copy = BILLING_ACTION_COPY[kind]

  const target =
    kind === "payment_action_required"
      ? resolveConfirmPaymentTarget(details)
      : { path: resolveBillingPath(details) }

  return [
    {
      id: copy.id,
      rowLabel: "Billing",
      buttonLabel: copy.buttonLabel,
      path: target.path,
      ...(target.external ? { external: true } : {}),
    },
  ]
}
